import { fallbackWheelCardConfig } from './config/fallbackConfig'

import type { SectionType, SectionDetails, WheelSectionDetails } from 'store/modules/bonusesFlow/types'
import type { WheelCardConfig } from 'store/modules/brandConfig/types'

export const loadFont = async (alias: string, path: string) => {
  const fontFaceInstance = new FontFace(alias, `url(${path})`)
  try {
    await fontFaceInstance.load()

    document.fonts.add(fontFaceInstance)

    await document.fonts.ready
  } catch (error) {
    // console.log('Something went wrong...')
  }
}

export const t = (cfg: WheelCardConfig) => (key: string) =>
  cfg?.translates?.[key] || fallbackWheelCardConfig.translates[key]

// parsing a bonus label string that contains "+ FREE"
function parseFreeLabel(text: string): {
  firstSubstring: string
  secondSubstring: string
  thirdSubstring: string
} {
  const FREE_PART = '+ FREE'
  const freeIndex = text.indexOf(FREE_PART)
  const gcPart = text.substring(0, freeIndex).trim()
  let scPart = text.substring(freeIndex + FREE_PART.length).trim()

  const match = scPart.match(/^SC\s+(\d+(?:\.\d+)?)/)

  if (match) {
    scPart = `SC ${parseFloat(match[1])}`
  }

  return {
    firstSubstring: gcPart,
    secondSubstring: FREE_PART,
    thirdSubstring: scPart,
  }
}

// parsing a bonus label string with format "GC 100k + SC 500"
function parseTopPrizeLabel(text: string): {
  firstSubstring: string
  secondSubstring: string
  thirdSubstring: string
} {
  const [gcPartRaw, scPartRaw] = text.split('+')

  const gcPart = gcPartRaw.trim() // e.g., "GC 100k"
  const scPart = scPartRaw.trim() // e.g., "SC 500"

  return {
    firstSubstring: gcPart,
    secondSubstring: '+',
    thirdSubstring: scPart,
  }
}

// splitting a bonus label text into separate parts, depending on the structure
export function splitBonusLabelText(text: string): {
  firstSubstring: string
  secondSubstring: string
  thirdSubstring?: string
} {
  const FREE_PART = '+ FREE'
  const BONUS_KEYWORDS = ['sc', 'gc', 'prize', 'free']
  const BONUS_TYPES = ['spins', 'draw']

  const trimmedText = text.trim()

  if (trimmedText.includes(FREE_PART)) {
    return parseFreeLabel(trimmedText)
  }

  const topPrizeParts = trimmedText.split('+')
  if (topPrizeParts.length === 2 && topPrizeParts[0].includes('GC') && topPrizeParts[1].includes('SC')) {
    return parseTopPrizeLabel(trimmedText)
  }

  const parts = trimmedText.split(/\s+/)

  // Case: "10 SC SPINS" or "10 GC SPINS" or "10 Draw Entries"
  if (parts.length >= 3) {
    const [amount, second, third, ...rest] = parts
    const amountIsNumber = !isNaN(Number(amount))

    if (
      amountIsNumber &&
      (BONUS_KEYWORDS.includes(second.toLowerCase()) || BONUS_TYPES.includes(second.toLowerCase()))
    ) {
      return {
        firstSubstring: `${amount} ${second.toUpperCase()}`,
        secondSubstring: [third, ...rest].join(' ').trim(),
      }
    }
  }

  return {
    firstSubstring: parts[0] || '',
    secondSubstring: parts.slice(1).join(' ') || '',
  }
}

export const getSectionDetails = (type: SectionType, details: WheelSectionDetails): SectionDetails | null => {
  const typeMap = {
    gold_sweepstake_coins: 'goldSweepstakeCoins',
    free_spins: 'freeSpins',
    purchase_offer: 'purchaseOffer',
    prize_draw: 'prizeDraw',
  } as const
  const propertyName = typeMap[type]
  if (propertyName && propertyName in details) {
    return { type, details: details[propertyName as keyof WheelSectionDetails] }
  }

  return null
}

export const isFreeSpinsWin = (sectionDetails?: SectionDetails): boolean => {
  return sectionDetails?.type === 'free_spins'
}

export const isPurchaseOfferWin = (sectionDetails?: SectionDetails): boolean => {
  return sectionDetails?.type === 'purchase_offer'
}
